type SkeletonVariant = "line" | "card" | "circle";

interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  className?: string;
}

export function Skeleton({ variant = "line", width, height, className = "" }: SkeletonProps) {
  const variantDefaults: Record<SkeletonVariant, string> = {
    line: "h-4 w-full rounded",
    card: "h-32 w-full rounded-lg",
    circle: "h-10 w-10 rounded-full",
  };

  return (
    <div
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${variantDefaults[variant]} ${className}`}
      style={{
        ...(width ? { width: typeof width === "number" ? `${String(width)}px` : width } : {}),
        ...(height ? { height: typeof height === "number" ? `${String(height)}px` : height } : {}),
      }}
    />
  );
}

interface SkeletonTableProps {
  columns: number;
  rows?: number;
}

export function SkeletonTable({ columns, rows = 5 }: SkeletonTableProps) {
  return (
    <div
      data-testid="skeleton-table"
      className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
    >
      {/* Header */}
      <div
        data-testid="skeleton-table-header"
        className="flex gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800"
      >
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={`header-${String(i)}`} width="80px" height="12px" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, ri) => (
        <div
          key={`row-${String(ri)}`}
          className="flex gap-4 border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-gray-700"
        >
          {Array.from({ length: columns }).map((_, ci) => (
            <Skeleton key={`cell-${String(ri)}-${String(ci)}`} width="96px" height="16px" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Title */}
      <Skeleton width="240px" height="36px" />
      {/* Content */}
      <Skeleton variant="card" />
      <Skeleton variant="card" height="200px" />
    </div>
  );
}
